import { z } from "zod";
import { invitationItemSchema } from "../invitation.schemas";

export type InvitationUsageContext = {
  /** Maximum number of uses allowed (null = unlimited) */
  maxUses: number | null;
  /** Number of invitations already used (pending + claimed records) */
  usedCount: number;
};

/**
 * Calculate remaining invitation slots based on usage context.
 * Returns null for unlimited invitations.
 */
export function calculateRemainingSlots(
  context: InvitationUsageContext
): number | null {
  if (context.maxUses === null) return null;
  return Math.max(0, context.maxUses - context.usedCount);
}

/**
 * Build an invitations schema with a limit on the number of valid emails.
 * Extends the base invitationsSchema with an additional refinement.
 */
export function buildInvitationsSchemaWithLimit(
  context: InvitationUsageContext
) {
  const remainingSlots = calculateRemainingSlots(context);

  return z.object({
    items: z
      .array(invitationItemSchema)
      // Existing: at least one valid email
      .refine(
        (items) =>
          items.some(
            (item) =>
              item.email.trim() !== "" &&
              z.string().email().safeParse(item.email.trim()).success
          ),
        { message: "At least one valid email is required", path: ["root"] }
      )
      // New: don't exceed remaining slots
      .refine(
        (items) => {
          if (remainingSlots === null) return true;
          const validCount = items.filter(
            (item) =>
              item.email.trim() !== "" &&
              z.string().email().safeParse(item.email.trim()).success
          ).length;
          return validCount <= remainingSlots;
        },
        {
          message:
            remainingSlots === 0
              ? "No invitation slots remaining"
              : `You can only send ${remainingSlots} more invitation${remainingSlots === 1 ? "" : "s"}`,
          path: ["root"],
        }
      ),
  });
}
